// display.heading.v1
//
// Semantic heading with three preset sizes. The biggest one — `page` —
// uses the editorial serif italic the PluginPageHeader uses, so the
// hierarchy reads consistently. `section` and `subsection` use the
// regular sans for body-level structure.

import { type JSX, splitProps } from "solid-js"
import { Dynamic } from "solid-js/web"
import type { HeadingIntent } from "../types"

export interface HeadingProps {
  intent?: HeadingIntent
  children: JSX.Element
  class?: string
  id?: string
}

const sizeClass = {
  page: "font-display italic text-text-strong text-[34px] leading-[1.08]",
  section: "text-18-medium text-text-strong",
  subsection: "text-15-medium text-text-strong",
} as const satisfies Record<HeadingIntent, string>

const tagFor = (intent: HeadingIntent | undefined) => {
  if (intent === "page") return "h1"
  if (intent === "section") return "h2"
  return "h3"
}

export function Heading(props: HeadingProps) {
  const [local, rest] = splitProps(props, ["intent", "children", "class", "id"])
  const intent = local.intent ?? "section"
  const tag = tagFor(intent)

  return (
    <Dynamic
      component={tag}
      data-registry-id="display.heading"
      data-intent={intent}
      id={local.id}
      classList={{
        [sizeClass[intent]]: true,
        [local.class ?? ""]: !!local.class,
      }}
      {...rest}
    >
      {local.children}
    </Dynamic>
  )
}
